import React from "react";
import api from "../api";

const S3_ORIGIN = "https://hhcourses-assets.s3.us-east-2.amazonaws.com";
const CLOUDFRONT_ORIGIN = "https://d10jmd9bhq0s4v.cloudfront.net";

export const toCloudFront = (url) =>
  (url || "").replace(S3_ORIGIN, CLOUDFRONT_ORIGIN);

export const toS3Origin = (url) =>
  (url || "").replace(CLOUDFRONT_ORIGIN, S3_ORIGIN);

/** Live covers overwrite a stable key. Read S3 and bust the browser cache. */
export const liveCoverSrc = (url, bust = 0) => {
  const origin = toS3Origin(url);
  if (!origin) return "";
  const sep = origin.includes("?") ? "&" : "?";
  return bust ? `${origin}${sep}v=${bust}` : origin;
};

const errorMessage = (err) =>
  err?.response?.data?.error || err?.message || "Something went wrong";

export const ORIGINAL_COVER_INDEX = -1;

export function mergeCoverVariants(prev, incoming) {
  const byUrl = new Map();
  const order = [];
  const add = (item) => {
    const url = item?.url;
    if (!url) return;
    if (byUrl.has(url)) {
      const existing = byUrl.get(url);
      byUrl.set(url, {
        url,
        concept: item.concept || existing.concept || "",
        createdAt: item.createdAt || existing.createdAt,
      });
      return;
    }
    byUrl.set(url, {
      url,
      concept: item.concept || "",
      createdAt: item.createdAt,
    });
    order.push(url);
  };
  (prev || []).forEach(add);
  (incoming || []).forEach(add);
  return order.map((url) => byUrl.get(url));
}

export function syncCoverVariantsFromS3(prev, listed) {
  const prevByUrl = new Map(
    (prev || []).filter((item) => item?.url).map((item) => [item.url, item])
  );
  const listedUrls = new Set((listed || []).map((item) => item?.url).filter(Boolean));
  const fromList = (listed || [])
    .filter((item) => item?.url)
    .map((item) => {
      const existing = prevByUrl.get(item.url);
      return {
        url: item.url,
        concept: existing?.concept || item.concept || "",
        createdAt: item.createdAt || existing?.createdAt,
      };
    });
  const extras = (prev || []).filter((item) => item?.url && !listedUrls.has(item.url));
  return [...fromList, ...extras];
}

/**
 * Left-pane controls for regenerating a course cover: seed brief, generate 3
 * distinct options, pick one, publish and delete the rest. The live cover stays
 * in the picker so it can be kept and the new variants discarded.
 */
export const CourseCoverControls = ({
  selectedCourse,
  coverConcept,
  setCoverConcept,
  extraGuidance,
  setExtraGuidance,
  coverOptions,
  setCoverOptions,
  selectedCoverIndex,
  setSelectedCoverIndex,
  coverBusy,
  setCoverBusy,
  coverError,
  setCoverError,
  onCourseUpdated,
  coverCacheBust = 0,
  importMode = false,
  onApprove,
  approving = false,
  approved = false,
}) => {
  const originalUrl = selectedCourse?.courseImage || "";
  const keepOriginal = selectedCoverIndex === ORIGINAL_COVER_INDEX;

  const handleGenerate = async () => {
    if (!selectedCourse?._id || !coverConcept.trim()) return;
    setCoverBusy("generate");
    setCoverError(null);
    try {
      const response = await api.post(`/api/admin/courses/${selectedCourse._id}/cover`, {
        coverConcept,
        extraGuidance,
        existingCount: coverOptions.length,
        existingConcepts: coverOptions.map((option) => option.concept).filter(Boolean),
      });
      const next = response.data.variants || [];
      setCoverOptions((prev) => mergeCoverVariants(prev, next));
      setSelectedCoverIndex(coverOptions.length);
    } catch (err) {
      setCoverError(errorMessage(err));
    } finally {
      setCoverBusy(null);
    }
  };

  const handlePublish = async () => {
    const selectedUrl = keepOriginal ? originalUrl : coverOptions[selectedCoverIndex]?.url;
    if (!selectedCourse?._id || !selectedUrl) return;

    setCoverBusy("publish");
    setCoverError(null);
    try {
      const response = await api.post(
        `/api/admin/courses/${selectedCourse._id}/cover/publish`,
        {
          selectedUrl,
          rejectedUrls: coverOptions
            .filter((option, index) => keepOriginal || index !== selectedCoverIndex)
            .map((option) => option.url),
        }
      );
      setCoverOptions([]);
      setSelectedCoverIndex(0);
      if (onCourseUpdated) onCourseUpdated(response.data);
    } catch (err) {
      setCoverError(errorMessage(err));
    } finally {
      setCoverBusy(null);
    }
  };

  const hasOptions = coverOptions.length > 0;
  const generateLabel = coverBusy === "generate"
    ? "Generating…"
    : hasOptions
      ? "↺ 3 More"
      : "Generate 3 Options";

  return (
    <div className="course-workspace-controls">
      <h3 className="course-workspace-heading">Course Cover</h3>
      <p className="course-workspace-hint">
        Generate three distinct cover varieties, pick the one you like, then save it.
        The current cover stays in the list so you can keep it. Unused options are deleted from storage.
      </p>

      {hasOptions && (
        <div className="course-cover-confirm-notice">
          Unpublished covers are still in storage. Pick one option or keep the current cover,
          then confirm so the others can be deleted.
        </div>
      )}

      {coverError && <div className="generation-error">{coverError}</div>}

      <div className="course-settings-label">Cover concept</div>
      <textarea
        className="course-settings-input"
        rows={4}
        value={coverConcept}
        onChange={(e) => setCoverConcept(e.target.value)}
        placeholder="What the central illustration should show"
        disabled={coverBusy === "generate"}
      />

      <div className="course-settings-label">Extra art direction (optional)</div>
      <textarea
        className="course-settings-input"
        rows={2}
        value={extraGuidance}
        onChange={(e) => setExtraGuidance(e.target.value)}
        placeholder="e.g. cooler palette, more negative space"
        disabled={coverBusy === "generate"}
      />

      <button
        className="generate-images-button"
        onClick={handleGenerate}
        disabled={coverBusy === "generate" || !coverConcept.trim() || !selectedCourse?._id}
      >
        {generateLabel}
      </button>

      {hasOptions && (
        <>
          <div className="bulk-cover-grid course-cover-workspace-grid">
            {originalUrl && (
              <button
                type="button"
                className={`bulk-cover-option is-original ${keepOriginal ? "selected" : ""}`}
                onClick={() => setSelectedCoverIndex(ORIGINAL_COVER_INDEX)}
                title="Current published cover"
              >
                <img src={liveCoverSrc(originalUrl, coverCacheBust)} alt="Current course cover" />
                <span className="course-cover-option-caption">Current cover</span>
              </button>
            )}
            {coverOptions.map((option, index) => (
              <button
                key={option.url}
                type="button"
                className={`bulk-cover-option ${selectedCoverIndex === index ? "selected" : ""}`}
                onClick={() => setSelectedCoverIndex(index)}
                title={option.concept || `Option ${index + 1}`}
              >
                <img src={toCloudFront(option.url)} alt={`Cover option ${index + 1}`} />
                <span className="course-cover-option-caption">
                  {option.concept || `Option ${index + 1}`}
                </span>
              </button>
            ))}
          </div>
          {!importMode && (
            <button
              className="save-lesson-button"
              onClick={handlePublish}
              disabled={coverBusy === "publish" || (!keepOriginal && !coverOptions[selectedCoverIndex])}
            >
              {coverBusy === "publish"
                ? "Saving…"
                : keepOriginal
                  ? "Keep Current Cover"
                  : "Use This Cover"}
            </button>
          )}
        </>
      )}

      {importMode && !approved && (
        <button
          className="bulk-btn bulk-btn-copy"
          onClick={onApprove}
          disabled={
            approving ||
            Boolean(coverBusy) ||
            (!keepOriginal && !coverOptions[selectedCoverIndex] && !originalUrl)
          }
        >
          {approving ? "Approving…" : "✓ Approve Cover"}
        </button>
      )}
    </div>
  );
};

export const CourseCoverPreview = ({
  selectedCourse,
  coverOptions,
  selectedCoverIndex,
  coverCacheBust = 0,
}) => {
  const keepOriginal = selectedCoverIndex === ORIGINAL_COVER_INDEX;
  const selected = keepOriginal ? null : coverOptions[selectedCoverIndex];
  const previewUrl = selected?.url || selectedCourse?.courseImage;
  const previewSrc = selected
    ? toCloudFront(previewUrl)
    : liveCoverSrc(previewUrl, coverCacheBust);

  return (
    <div className="course-cover-preview">
      {previewUrl ? (
        <>
          <img
            src={previewSrc}
            alt={
              keepOriginal || !selected
                ? "Current course cover"
                : `Cover option ${(selectedCoverIndex || 0) + 1}`
            }
          />
          <div className="course-cover-preview-caption">
            {keepOriginal || !selected
              ? "Current cover"
              : selected.concept || `Option ${(selectedCoverIndex || 0) + 1}`}
          </div>
        </>
      ) : (
        <div className="course-settings-empty-cover course-cover-preview-empty">
          No cover set. Generate options to preview them here.
        </div>
      )}
    </div>
  );
};
